import { ShowAllProductsSelected } from '../models/show-all-products-selected.interface';
import moment from 'moment';
import { isNil } from 'src/utils/utils';

export type UserSection = 'online' | 'app' | 'delivery';
export type DeliveryContingentType = 'sms' | 'fax';

export const USER_SECTIONS: UserSection[] = ['online', 'app', 'delivery'];
export const DELIVERY_CONTINGENT_TYPES: DeliveryContingentType[] = [
  'sms',
  'fax',
];

export const EMPTY_CONTINGENTS = {
  online: 0,
  app: 0,
  sms: 0,
  fax: 0,
};

export const DEFAULT_CONTINGENTS = {
  online: 3,
  app: 3,
  sms: 0,
  fax: 0,
};

export const INITIAL_PRODUCT_LIST = {
  eop: {},
  epp: {},
  egp: [],
  egpsummer: [],
  egw: [],
  egwsummer: [],
  smw: { gma: [], synop: [] },
  radar: [],
  marwis: [],
  route: [],
  tauben: [],
  ampel: [],
  egwgma: [],
  trend: [],
};

export const USER_ADMIN_GROUP = 'EINSATZWETTER_USER_ADMIN';

export const USER_PRODUCT_TYPES = {
  eop: 'eopProducts',
  epp: 'eppProducts',
  egp: 'egpProducts',
  egpSummer: 'egpSummerProducts',
  egw: 'egwProducts',
  egwSummer: 'egwSummerProducts',
  smw: 'smwProducts',
  radar: 'radarProducts',
  marwis: 'marwisProducts',
  route: 'routeProducts',
  pigeon: 'pigeonProducts',
  trafficLights: 'trafficLightsProducts',
  trend: 'trendProducts',
  egwGma: 'egwGmaProducts',
} as const;

export const PRODUCT_LIST_KEYS = {
  eop: 'eop',
  epp: 'epp',
  egp: 'egp',
  egpSummer: 'egpsummer',
  egw: 'egw',
  egwSummer: 'egwsummer',
  smw: 'smw',
  radar: 'radar',
  marwis: 'marwis',
  route: 'route',
  pigeon: 'tauben',
  trafficLights: 'ampel',
  trend: 'trend',
  egwGma: 'egwgma',
} as const;

export const egpTypes = {
  egp: 'WINTER',
  egpsummer: 'SUMMER',
  egw: 'WINTER',
  egwsummer: 'SUMMER',
};

export const eopTypes = [
  'TWO_DAY_ALL',
  'TWO_DAY',
  'TWO_DAY_SUMMER',
  'FOUR_DAY_ALL',
  'FOUR_DAY',
  'FOUR_DAY_SUMMER',
  'SIX_DAY_ALL',
  'SIX_DAY',
  'SIX_DAY_SUMMER',
  'EIGHT_DAY_ALL',
  'EIGHT_DAY',
  'EIGHT_DAY_SUMMER',
  'FIFTEEN_DAY_ALL',
  'FIFTEEN_DAY',
  'FIFTEEN_DAY_SUMMER',
];

export const contactSubjects = [
  'general',
  'userContingent',
  'appContingent',
  'smsContingent',
  'faxContingent',
  'other',
];

export const createEopFormControlName = (
  eopType: string,
  productId: number,
  stationId: string
): string => `ADDRESSESxEOPx${eopType}x${productId}x${stationId}`;

export const createEppFormControlName = (
  resolutionKey: string,
  parameterset: string,
  stationId: string
): string => `ADDRESSESxEPPx${resolutionKey}x${parameterset}x${stationId}`;

export const createEgpFormControlName = (productId: number): string =>
  `ADDRESSESxEGPx${productId}`;

export const createEgpSummerFormControlName = (productId: number): string =>
  `ADDRESSESxEGPSUMMERx${productId}`;

export const createEgwFormControlName = (productId: number): string =>
  `ADDRESSESxEGWx${productId}`;

export const createEgwSummerFormControlNames = (productId: number) => ({
  formControlName: `ADDRESSESxEGWSUMMERx${productId}`,
  infoFormControlName: `ADDRESSESxEGWSUMMERx${productId}info`,
  nowcastFormControlName: `ADDRESSESxEGWSUMMERx${productId}xNOWCAST`,
  heavyRainWarningFormControlName: `ADDRESSESxEGWSUMMERx${productId}xHEAVYRAINWARNING`,
  precipitationWarningFormControlName: `ADDRESSESxEGWSUMMERx${productId}xPRECIPITATIONWARNING`,
  windWarningFormControlName: `ADDRESSESxEGWSUMMERx${productId}xWINDWARNING`,
});

export const createEgwGmaFormControlName = (
  productId: number,
  stationId: string
): string => `ADDRESSESxEGWGMAx${productId}x${stationId}`;

export const toEppResolutionKey = (resolution: any): string =>
  `${resolution.days}_${resolution.hourly}`;

export const sortByArea = <T extends { area: string }>(a: T, b: T): number =>
  a.area < b.area ? -1 : 1;

export const sortStationsByName = <T extends { overwriteStationName: string }>(
  stations: T[]
): T[] =>
  stations.sort((a, b) =>
    a.overwriteStationName < b.overwriteStationName ? -1 : 1
  );

export const sortProductsByStationName = <
  T extends { station: { overwriteStationName: string } },
>(
  products: T[]
): T[] =>
  products.sort((a, b) =>
    a.station.overwriteStationName < b.station.overwriteStationName ? -1 : 1
  );

export const findAreaName = (
  areas: any[],
  areaId: string | number,
  strict = false
): string => {
  const area = areas.find(currentArea =>
    strict ? currentArea['areaId'] === areaId : currentArea['areaId'] == areaId
  );

  return area?.['name'];
};

export const getShowUsers = (
  section: string,
  id: string,
  showUsers: any
): boolean => showUsers?.[section]?.[id] === true;

export const isUserAdmin = (userData: any): boolean =>
  userData?.groups?.includes(USER_ADMIN_GROUP) ?? false;

export const hasUserAccess = (section: string, userData): boolean => {
  if (section !== 'online' && !userData?.metadata?.[section]) {
    return false;
  }

  return userData?.metadata?.[section]?.active !== false;
};

export const isUserAllowedToAccessAllProducts = (
  section: string,
  userData: any
): boolean => isNil(userData?.metadata?.[section]?.product);

export const userActiveProductIds = (
  section: string,
  userData: any,
  options: any = {}
): any[] => {
  const products = userData?.metadata?.[section]?.product ?? [];

  return products.reduce((activeProducts, product) => {
    if (!product?.active) {
      return activeProducts;
    }

    if (options.hasOwnProperty('parameterset')) {
      if (
        product?.parameterset === options.parameterset &&
        product?.resolution?.days === options.resolution?.days
      ) {
        activeProducts.push({
          id: product.id,
          parameterset: options.parameterset,
          resolution: options.resolution,
        });
      }

      return activeProducts;
    }

    activeProducts.push(product.id);
    return activeProducts;
  }, []);
};

export const countUsersForProduct = (
  section: string,
  productId: number,
  productViewUserData: any,
  stationId: string = null
): number => {
  let count =
    section !== 'delivery'
      ? (productViewUserData?.[section]?.usersWithFullAccess?.length ?? 0)
      : 0;

  const productUsers = productViewUserData?.[section]?.[productId];

  if (!productUsers) {
    return count;
  }

  if (stationId === null && Array.isArray(productUsers)) {
    count += productUsers.length;
  }

  if (stationId !== null && Array.isArray(productUsers?.[stationId])) {
    count += productUsers[stationId].length;
  }

  return count;
};

export const userNameOfId = (userId: number, userList: any[]): string => {
  const user = userList.find(currentUser => currentUser.id === userId);
  return user ? getUserName(user) : '';
};

export const getUserName = (userData): string => userData?.metadata?.name ?? '';

export const userStatusIcon = (userData): any => {
  const metadata = userData?.metadata;

  if (!metadata || metadata.active === null || metadata.active === undefined) {
    return { icon: 'question', color: 'red' };
  }

  if (!metadata.active) {
    return { icon: 'times', color: 'red' };
  }

  const today = moment();
  const startDate = moment(metadata.start);

  if (startDate.isAfter(today)) {
    return { icon: 'calendar-star', color: 'grey' };
  }

  if (!metadata.end || metadata.end === 'Invalid date') {
    return { icon: 'infinity', color: 'green' };
  }

  const endDate = moment(metadata.end);

  if (endDate.isBefore(today)) {
    return { icon: 'calendar-times', color: 'red' };
  }

  if (endDate.isAfter(moment('2100-01-01'))) {
    return { icon: 'infinity', color: 'green' };
  }

  return { icon: 'calendar-check', color: 'green' };
};

export const hasUserProduct = (
  section: string,
  areAllProductsSelected: ShowAllProductsSelected,
  userServiceUserData: any,
  productId
): boolean => {
  if (areAllProductsSelected[section]) {
    return true;
  }

  const productIds = userActiveProductIds(section, userServiceUserData);
  return productIds.includes(productId);
};

export const hasUserStation = (
  userData,
  product,
  section: string,
  areAllProductsSelected
): boolean => {
  if (areAllProductsSelected[section]) {
    return true;
  }

  const productId = product['id'];
  const stationId = product['station']['stationId'];
  const userProducts = userData?.metadata?.[section]?.product ?? [];

  for (const userProduct of userProducts) {
    if (userProduct?.id !== productId || isNil(userProduct?.station)) {
      continue;
    }

    for (const userStation of userProduct.station) {
      if (stationId !== userStation) {
        continue;
      }

      if (product.hasOwnProperty('parameterset') && section === 'delivery') {
        const hasMatchingParameterset =
          userProduct.parameterset === product.parameterset;
        const hasMatchingResolution =
          userProduct?.resolution?.days === product?.resolution?.days;

        if (hasMatchingParameterset && hasMatchingResolution) {
          return true;
        }

        continue;
      }

      return true;
    }
  }

  return false;
};

export const verifyPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber === null || phoneNumber === '') {
    return null;
  }

  let verifiedNumber = phoneNumber.replace(/[ ()/.-]/g, '');

  if (verifiedNumber.charAt(0) === '+') {
    verifiedNumber = verifiedNumber.replace('+', '');
  }

  if (verifiedNumber.startsWith('00')) {
    verifiedNumber = verifiedNumber.substring(2);
  }

  if (verifiedNumber.charAt(0) === '0') {
    verifiedNumber = `49${verifiedNumber.substring(1)}`;
  }

  return verifiedNumber.match(/^(49|41)(\d+)$/) ? verifiedNumber : null;
};
